import { markWatchedCards } from './watched';

function sortedPins(hero: Element): HTMLElement[] {
  return [...hero.querySelectorAll<HTMLElement>('.js-map-pin')].sort(
    (a, b) => Number(a.dataset.order ?? 0) - Number(b.dataset.order ?? 0),
  );
}

const MAP_AR = 1440 / 1024;
const CLUSTER = { x0: 0.42, x1: 0.83, y0: 0.14, y1: 0.95 };
const CLUSTER_W = CLUSTER.x1 - CLUSTER.x0;
const CLUSTER_H = CLUSTER.y1 - CLUSTER.y0;
// Clearance so the cluster doesn't touch the frame edges — hover name-pills expand
// centered on the pin, so each needs room on both sides (PAD_X) plus the pill
// height + active pulse ring (PAD_Y).
const PAD_X = 100;
const PAD_Y = 46;
// Pin sizing tracks the map width: full size at the design map width, scaling down
// to 0.85 over the range below it (--pin-scale, consumed in _homepage.scss).
const PIN_FULL_MAP_W = 1280;
const PIN_SCALE_RANGE = 380;

const clampCover = (v: number, lo: number, hi: number): number =>
  lo > hi ? v : Math.min(Math.max(v, lo), hi);

// Sizes the framed map "window" (.map-hero__region). The frame fills the width left
// over after the panel + a deliberate breathing gap, resting against the spine. The
// map scale is capped so the whole pin cluster fits the frame width (with PAD_X) AND
// the available height (with PAD_Y) — whichever is tighter — so every pin shows at
// any viewport ≥ 1150px. On tall/narrow viewports the frame HEIGHT shrinks (and
// re-centers vertically) to wrap the cluster; on wide viewports it fills the height.
// The oversized map always covers the frame (clipped to the rounded corners); stage
// + pins share the --map-* vars so the pin layer can overflow the frame on hover.
function layoutMap(hero: HTMLElement, panel: HTMLElement, spine: HTMLElement): void {
  const region = hero.querySelector<HTMLElement>('.map-hero__region');
  if (!region) return;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // The map is offset below the admin toolbar (--spine-top-offset), so the usable
  // height shrinks by the same amount — matches .map-hero's CSS top offset.
  const topOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--spine-top-offset')) || 0;
  const availH = Math.max(vh - topOffset, 0);
  const minInset = Math.min(Math.max(16, availH * 0.04), 40);
  const panelW = panel.offsetWidth;
  const availW = Math.max(vw - panelW - spine.offsetWidth, 0);

  const clusterCx = (CLUSTER.x0 + CLUSTER.x1) / 2;
  const clusterCy = (CLUSTER.y0 + CLUSTER.y1) / 2;

  // Gap between panel and map; the map fills the rest of the width to the spine.
  const gap = Math.min(Math.max(48, vw * 0.04), 112);
  const frameW = Math.max(availW - gap, 0);
  const maxFrameH = Math.max(availH - 2 * minInset, 0);

  // Scale so the cluster fits both the frame width (with PAD_X) and the available
  // height (with PAD_Y) — the tighter constraint wins — but never below covering
  // the frame width.
  const widthCapW = (frameW - 2 * PAD_X) / CLUSTER_W;
  const heightFitW = ((maxFrameH - 2 * PAD_Y) / CLUSTER_H) * MAP_AR;
  const mapW = Math.max(Math.min(widthCapW, heightFitW), frameW);
  const mapH = mapW / MAP_AR;

  // Frame height wraps the cluster (+PAD_Y) so all pins show on tall/narrow
  // viewports, but never taller than the map (cover) or the available height.
  const frameH = Math.min(maxFrameH, CLUSTER_H * mapH + 2 * PAD_Y, mapH);
  const inset = (availH - frameH) / 2;
  const insetPx = Math.round(inset);

  // Center the cluster in the frame so labels have equal room on all sides.
  const left = clampCover(frameW / 2 - clusterCx * mapW, frameW - mapW, 0);
  const top = clampCover(frameH / 2 - clusterCy * mapH, frameH - mapH, 0);
  const regionLeft = panelW + gap;

  region.style.setProperty('--map-inset', `${insetPx}px`);
  region.style.setProperty('--region-left', `${Math.round(regionLeft)}px`);
  region.style.setProperty('--region-width', `${Math.round(frameW)}px`);
  region.style.setProperty('--map-w', `${Math.round(mapW)}px`);
  region.style.setProperty('--map-left', `${Math.round(left)}px`);
  region.style.setProperty('--map-top', `${Math.round(top)}px`);

  // Shrink the pins in step with the map: at the design size (~1280px wide) they
  // stay full size; on smaller desktops where the map narrows they scale down to
  // a floor so the bubbles don't crowd the cluster.
  const pinScale = clampCover(0.85 + 0.15 * (mapW - PIN_FULL_MAP_W + PIN_SCALE_RANGE) / PIN_SCALE_RANGE, 0.85, 1);
  region.style.setProperty('--pin-scale', pinScale.toFixed(3));

  // Align the panel copy's bottom with the map's bottom edge: the map bottom sits
  // `inset` above the viewport bottom (symmetric region inset), and the panel
  // bottom is the viewport bottom — so the copy's bottom padding = that inset.
  panel.style.setProperty('--panel-pad-bottom', `${insetPx}px`);
}

function initHomepage(): void {
  const hero = document.querySelector<HTMLElement>('.map-hero');
  const panel = document.querySelector<HTMLElement>('.js-panel');
  const spine = document.querySelector<HTMLElement>('.js-spine');

  if (!hero || !panel || !spine) return;

  const stage = hero.querySelector<HTMLElement>('.map-hero__stage');
  let activeColor: string | null = null;
  const tintOverlay = (color: string | null): void => {
    if (!stage) return;
    const next = color ?? activeColor;
    if (next) {
      stage.style.setProperty('--map-tint', next);
    } else {
      stage.style.removeProperty('--map-tint');
    }
  };

  const root = document.documentElement;
  const desktopMq = window.matchMedia('(width >= 1150px)');

  // All 13 state panels are pre-rendered server-side, so every card is in the
  // DOM at load — mark watched cards once here instead of after each injection.
  markWatchedCards(panel);

  let activePin: HTMLElement | null = null;
  let activePanel: HTMLElement | null = null;

  sortedPins(hero).forEach((pin, i) => {
    pin.style.setProperty('--pin-i', String(i));
  });

  function applyDesktop(): void {
    layoutMap(hero!, panel!, spine!);

    // Re-trigger the pin stagger (panel/map/spine are static now).
    root.classList.remove('homepage-animate');
    void root.offsetWidth;
    root.classList.add('homepage-animate');
  }

  function applyMobile(): void {
    root.classList.remove('homepage-animate');
  }

  function handleBreakpoint(e: MediaQueryListEvent | MediaQueryList): void {
    if (e.matches) {
      applyDesktop();
    } else {
      applyMobile();
    }
  }

  function setActivePinState(pin: HTMLElement | null): void {
    hero!.querySelectorAll<HTMLElement>('.js-map-pin').forEach((p) => {
      const isActive = p === pin;
      p.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      p.classList.toggle('is-active', isActive);
    });
  }

  // Hides the active state panel: [hidden] removes it from layout, inert removes
  // it from focus/AT, and dropping .is-active resets its enter animation.
  function deactivatePanel(p: HTMLElement): void {
    p.hidden = true;
    p.inert = true;
    p.classList.remove('is-active');
  }

  function closeStatePanel(): void {
    if (activePanel) {
      deactivatePanel(activePanel);
      activePanel = null;
    }
    panel!.classList.remove('panel--state');
    hero!.classList.remove('has-active-pin');
    setActivePinState(null);
    activePin = null;
    activeColor = null;
    tintOverlay(null);
  }

  function openStatePanel(slug: string, pin: HTMLElement): void {
    if (activePin === pin) {
      closeStatePanel();
      return;
    }

    const next = panel!.querySelector<HTMLElement>(`.js-panel-state[data-state="${slug}"]`);
    if (!next) return;

    if (activePanel) deactivatePanel(activePanel);

    // Dark variant drives the map's multiply-blend tint; light is the fallback.
    const dark = next.dataset.colorDark || next.dataset.colorLight || null;

    // Reveal the panel, then restart the enter animation (remove → reflow → add).
    next.hidden = false;
    next.inert = false;
    next.classList.remove('is-active');
    void next.offsetWidth;
    next.classList.add('is-active');
    // Reset scroll so the scroll-driven header shrink starts fresh.
    next.scrollTo({ top: 0 });
    activePanel = next;

    activePin = pin;
    activeColor = dark;
    tintOverlay(dark);
    setActivePinState(pin);
    hero!.classList.add('has-active-pin');
    panel!.classList.add('panel--state');
  }

  hero.querySelectorAll<HTMLElement>('.js-map-pin').forEach((pin) => {
    pin.setAttribute('aria-expanded', 'false');
    pin.addEventListener('click', (e) => {
      e.preventDefault();
      const slug = pin.dataset.state ?? '';
      if (slug) openStatePanel(slug, pin);
    });

    const color = pin.dataset.color ?? '';
    pin.addEventListener('pointerenter', () => tintOverlay(color));
    pin.addEventListener('pointerleave', () => tintOverlay(null));
    pin.addEventListener('focus', () => tintOverlay(color));
    pin.addEventListener('blur', () => tintOverlay(null));
  });

  handleBreakpoint(desktopMq);
  desktopMq.addEventListener('change', handleBreakpoint);

  let layoutRaf = 0;
  const scheduleLayout = (): void => {
    if (!desktopMq.matches) return;
    cancelAnimationFrame(layoutRaf);
    layoutRaf = requestAnimationFrame(() => layoutMap(hero, panel, spine));
  };

  window.addEventListener('resize', scheduleLayout);

  // admin-toolbar.ts writes the live toolbar height to --spine-top-offset on :root,
  // possibly after this runs and whenever Gin's trays toggle — re-fit the map then.
  new MutationObserver(scheduleLayout).observe(root, {
    attributes: true,
    attributeFilter: ['style'],
  });
}

document.addEventListener('DOMContentLoaded', initHomepage);
